<template>
  <div>
    <!-- Save Search Dialog -->
    <SaveSearchDialog
      v-model:visible="isSaveSearchModalVisible"
      @save="saveSearch"
    />

    <!-- Filters Drawer -->
    <FiltersDrawer
      v-model:visible="showFilters"
      v-model:selected-departements="filters.selectedDepartements"
      v-model:duration="filters.duration"
      v-model:start-date="filters.startDate"
      v-model:end-date="filters.endDate"
      v-model:source="filters.source"
      v-model:incomplete-address="filters.incompleteAddress"
      :saved-searches="savedSearches"
      @reset="resetFilters"
      @open-save-dialog="isSaveSearchModalVisible = true"
      @load-search="loadSavedSearch"
      @delete-search="deleteSavedSearch"
    />

    <!-- Hero section -->
    <HeroSection />

    <!-- Sticky filter bar -->
    <div ref="filterBarEl" class="filter-bar">
      <!-- Type chips -->
      <button
        class="chip"
        :class="{ 'chip-active': !filters.type || filters.type === 'ALL' }"
        @click="filters.type = 'ALL'"
      >
        Tous
      </button>
      <button
        class="chip"
        :class="{ 'chip-active': filters.type === 'onSite' }"
        @click="filters.type = 'onSite'"
      >
        Sur place
      </button>
      <button
        class="chip"
        :class="{ 'chip-active': filters.type === 'online' }"
        @click="filters.type = 'online'"
      >
        En ligne
      </button>

      <div class="filter-sep" />

      <!-- Duration chips -->
      <button
        class="chip"
        :class="{
          'chip-active': !filters.duration || filters.duration === 'ALL',
        }"
        @click="filters.duration = 'ALL'"
      >
        Tous
      </button>
      <button
        v-for="d in ['1', '2', '3']"
        :key="d"
        class="chip"
        :class="{ 'chip-active': filters.duration === d }"
        @click="filters.duration = d as EventFilters['duration']"
      >
        {{ d }}J
      </button>

      <div class="filter-sep" />

      <!-- Search input -->
      <div class="filter-search-wrap">
        <input
          ref="searchInput"
          v-model="filters.name"
          class="filter-search"
          type="text"
          placeholder="Rechercher un événement…"
          @keydown.esc="searchInput?.blur()"
        />
        <kbd class="filter-search-kbd">⌘ K</kbd>
      </div>

      <!-- Advanced filters button -->
      <button
        class="btn"
        :class="{ 'btn-primary': !areFiltersEmpty }"
        @click="showFilters = true"
      >
        Filtres<template v-if="!areFiltersEmpty">
          ({{ activeFiltersCount }})</template
        >
      </button>

      <!-- Clear filters -->
      <button v-if="!areFiltersEmpty" class="btn" @click="resetFilters">
        ✕
      </button>

      <div class="filter-sep" />

      <!-- Results count -->
      <span class="filter-results">
        {{ eventsResponse?.count ?? 0 }} résultat{{
          (eventsResponse?.count ?? 0) !== 1 ? 's' : ''
        }}
      </span>
    </div>

    <!-- Mobile: "À VENIR / FILTRER →" section header -->
    <div class="avenir-header">
      <span class="avenir-title">À VENIR</span>
      <button class="avenir-filter" @click="showFilters = true">
        FILTRER →
      </button>
    </div>

    <!-- Events list -->
    <EventsList
      :offset="offset"
      :events-response="eventsResponse"
      :is-loading="isLoading"
      :page-size="filters.size"
      :error="eventsError"
      @refresh="reloadEvents"
      @page-update="onPageUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { useStorage, useEventListener } from '@vueuse/core';
import type { EventFilters, EventItem, SavedSearch } from '@/types/Event';
import { API_ENDPOINTS } from '@/config/api';

definePageMeta({ layout: 'search' });

useSeoMeta({
  title:
    'Compétitions CrossFit en France 2026 | Calendrier Complet des Événements',
  description:
    'Trouvez toutes les compétitions CrossFit en France : dates, lieux, inscriptions. Calendrier mis à jour quotidiennement. Ne ratez plus aucun événement CrossFit !',
  keywords:
    'crossfit, compétition, france, événements, calendrier, crossfit france, compétition crossfit, événement sportif, crossfit 2026',
  ogTitle: 'Compétitions CrossFit France 2026 - Calendrier Complet',
  ogDescription:
    'Découvrez toutes les compétitions CrossFit en France sur une seule plateforme',
  ogType: 'website',
  ogUrl: 'https://crossfit-event-scanner.fr',
  twitterCard: 'summary_large_image',
});

interface FetchEventResponse {
  results: EventItem[];
  count: number;
}

const { fetchWithAuth } = useApiConfig();
const { add: notify } = useNotification();

const SEARCH_LOCAL_STORAGE_KEY = 'savedSearches';
const searchInput = ref<HTMLInputElement | null>(null);
const filterBarEl = ref<HTMLElement | null>(null);
const showFilters = ref(false);
const isSaveSearchModalVisible = ref(false);

useEventListener(document, 'keydown', (e: KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    searchInput.value?.focus();
    searchInput.value?.select();
  }
});

const {
  filters,
  filtersQuery,
  setFilters,
  resetFilters,
  initRouteSync,
  activeFiltersCount,
  areFiltersEmpty,
  offset,
  handlePageUpdate,
} = useFilters();

const savedSearches = useStorage<SavedSearch[]>(SEARCH_LOCAL_STORAGE_KEY, []);

function loadSavedSearch(searchFilters: EventFilters) {
  setFilters(searchFilters);
  showFilters.value = false;
}

function deleteSavedSearch(index: number) {
  savedSearches.value = savedSearches.value.filter((_, i) => i !== index);
}

function onPageUpdate(event: { page: number; rows: number }) {
  handlePageUpdate(event);
}

function saveSearch(newSearchName: string) {
  if (!newSearchName) return;
  savedSearches.value.push({ label: newSearchName, filters: filters.value });
  isSaveSearchModalVisible.value = false;
}

const {
  data: eventsResponse,
  status,
  error: eventsError,
  refresh: reloadEvents,
} = useAsyncData<FetchEventResponse>(
  'events',
  () =>
    fetchWithAuth<FetchEventResponse>(API_ENDPOINTS.EVENTS, {
      query: filtersQuery.value,
    }),
  { default: () => ({ results: [], count: 0 }) },
);

const isLoading = computed(
  () => status.value === 'pending' || status.value === 'idle',
);

watch(status, async (s) => {
  if (s === 'success') {
    window.scrollTo({
      top: (filterBarEl.value?.offsetTop ?? 0) - 64,
      behavior: 'smooth',
    });
  }
});

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: () =>
        JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Compétitions CrossFit en France',
          numberOfItems: eventsResponse.value?.count || 0,
          itemListElement:
            eventsResponse.value?.results.slice(0, 10).map((event, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: useEventSchema(event),
            })) || [],
        }),
    },
  ],
});

watch(eventsError, (error) => {
  if (error) notify(extractApiErrorMessage(error), 'error');
});

initRouteSync(reloadEvents);
</script>
